import React, { useState, useEffect, useRef } from 'react';
import './ProductImagesModal.css';

const ADMIN_API_URL = "https://ewpfvffejfybyxcgnhim2bwwyi0gybco.lambda-url.eu-west-2.on.aws";

export default function ProductImagesModal({ productId, onClose, userData, onUpdate }) {
  const [images, setImages] = useState([]);
  const [mainImage, setMainImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [maxImages, setMaxImages] = useState(4);
  const [uploadStatus, setUploadStatus] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchProductImages();
  }, [productId]);

  const fetchProductImages = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${ADMIN_API_URL}/admin/products/${productId}/images`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userData.token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch product images');
      }

      const data = await response.json();
      setMainImage(data.mainImage);
      setImages(data.images || []);
      setMaxImages(data.maxImages || 4);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching images:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Перевірка підтримуваних форматів
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setUploadStatus({
        success: false,
        message: 'Only PNG, JPEG, JPG and WEBP files are allowed'
      });
      return;
    }

    try {
      setUploadStatus({ loading: true });
      
      // Конвертуємо файл у base64
      const base64Image = await convertFileToBase64(file);
      const base64String = base64Image.split(',')[1]; // Видаляємо префікс "data:image/png;base64,"

      // Відправляємо на сервер
      const response = await fetch(`${ADMIN_API_URL}/admin/products/${productId}/additional`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userData.token}`
        },
        body: JSON.stringify({
          image: base64String
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to upload image');
      }

      setUploadStatus({
        success: true,
        message: 'Image uploaded successfully'
      });
      
      // Оновлюємо стан зображень
      setImages(data.images || []);
      
      // Викликаємо колбек для оновлення батьківського компонента
      if (onUpdate) onUpdate();

      // Очищаємо input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      // Автоматично приховуємо повідомлення через 3 секунди
      setTimeout(() => {
        setUploadStatus(null);
      }, 3000);
    } catch (err) {
      console.error('Error uploading image:', err);
      setUploadStatus({
        success: false,
        message: err.message
      });
    }
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleDeleteImage = async (imageUrl) => {
    if (!window.confirm('Are you sure you want to delete this image?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${ADMIN_API_URL}/admin/products/${productId}/images/delete?url=${encodeURIComponent(imageUrl)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${userData.token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete image');
      }

      const data = await response.json();
      
      // Update both main image and additional images
      if (imageUrl === mainImage) {
        setMainImage(null);
      } else {
        setImages(data.images || []);
      }
      
      // Refresh the images
      fetchProductImages();
      
      // Викликаємо колбек для оновлення батьківського компонента
      if (onUpdate) onUpdate();
      
      setLoading(false);
    } catch (err) {
      console.error('Error deleting image:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div className="product-images-modal" onClick={onClose}>
      <div className="modal-content images-modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Product Images</h2>
          <div className="image-count">
            {images.length} / {maxImages} images
          </div>
          <button className="close-modal" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-body">
          {error && (
            <div className="error-message">
              Error: {error}
            </div>
          )}
          
          {uploadStatus && (
            <div className={`upload-status-message ${uploadStatus.success ? 'success' : 'error'}`}>
              {uploadStatus.loading ? (
                <div className="loading-spinner"></div>
              ) : (
                uploadStatus.message
              )}
            </div>
          )}
          
          <div className="images-container">
            {loading ? (
              <div className="loading-spinner-container">
                <div className="loading-spinner"></div>
                <p>Loading images...</p>
              </div>
            ) : (
              <>
                {/* Additional Images Section */}
                <div className="additional-images-section">
                  <div className="section-header">
                    <h3>Product Images</h3>
                    <div className="additional-actions">
                      {(images.length + (mainImage ? 1 : 0) < maxImages) && (
                        <button 
                          className="btn-upload" 
                          onClick={handleUploadClick}
                        >
                          Upload New Image
                        </button>
                      )}
                      <input 
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                      />
                    </div>
                  </div>
                  
                  <div className="additional-images-grid">
                    {images.length === 0 && !mainImage ? (
                      <div className="no-additional-images">
                        <p>No images</p>
                      </div>
                    ) : (
                      <>
                        {mainImage && (
                          <div className="additional-image-item">
                            <div className="image-wrapper">
                              <img 
                                src={mainImage} 
                                alt="Main product" 
                                className="additional-image"
                              />
                            </div>
                            <div className="image-actions">
                              <button 
                                className="btn-delete"
                                onClick={() => handleDeleteImage(mainImage)}
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        )}
                        
                        {images.map((imageUrl, index) => (
                          <div className="additional-image-item" key={index}>
                            <div className="image-wrapper">
                              <img 
                                src={imageUrl} 
                                alt={`Product ${index + 1}`} 
                                className="additional-image"
                              />
                            </div>
                            <div className="image-actions">
                              <button 
                                className="btn-delete"
                                onClick={() => handleDeleteImage(imageUrl)}
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
} 